/**
 * Auth-aware Query Hook Utilities
 *
 * Provides utilities for creating queries that automatically
 * wait for authentication before executing.
 */

import { useAuth } from '@/lib/auth';

/**
 * Hook to get auth-aware enabled state for queries.
 *
 * Combines the auth state with custom enabled conditions.
 * Queries will not run until the user is authenticated.
 *
 * @param customEnabled - Additional enabled condition (default: true)
 * @returns Object with enabled flag and isAuthenticated state
 *
 * @example
 * ```tsx
 * const { enabled, isAuthenticated } = useAuthEnabled();
 *
 * const { data } = useQuery({
 *   queryKey: ['datasets'],
 *   queryFn: fetchDatasets,
 *   enabled,
 * });
 * ```
 */
export function useAuthEnabled(customEnabled = true) {
  const { isAuthenticated, isLoading } = useAuth();

  return {
    // Only enable when authenticated AND custom condition is met AND not loading
    enabled: isAuthenticated && customEnabled && !isLoading,
    isAuthenticated,
    isLoading,
  };
}
